import React, { useState } from 'react'

import * as c from '../common'
import * as r from '../react-utils'
import {
  SigningSection,
  sanitizeSigningSections,
} from '../common/helpers/signingSections'

import { FirebaseOptions, initializeApp } from 'firebase/app'
import {
  User,
  createUserWithEmailAndPassword as _createUserWithEmailAndPassword,
  Auth,
  getAuth,
  inMemoryPersistence,
  initializeAuth,
  signInWithCustomToken as _signInWithCustomToken,
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
} from 'firebase/auth'
import {
  DatabaseReference as V9DatabaseReference,
  QueryConstraint,
  child,
  get,
  getDatabase,
  limitToFirst,
  off,
  onChildAdded,
  onChildMoved,
  onChildChanged,
  onChildRemoved,
  onValue,
  orderByChild,
  //  push,
  ref,
  remove,
  set,
  update,
  query,
  limitToLast,
} from 'firebase/database'
import {
  getMetadata as storageGetMetadata,
  getStorage,
  uploadBytesResumable,
  ref as storageRef,
} from 'firebase/storage'
import { Navigate, useLocation } from 'react-router-dom'

// Same fixed uid every public /sign/:token session authenticates as
// (src/back/sys.ts SIGNER_UID default). Used here only to detect "this is a
// customer signing session" so RequireAuth can pin it to its own report and
// block navigating elsewhere in the app — see V50/B77.
export const SIGNER_UID = 'qlX0Itwyxgb9dsoTusUeTVQbnmU2'
// Set by SignContractPage right after a successful custom-token sign-in.
export const SIGNING_CUSTOMER_ID_KEY = 'ecohomeSigningCustomerID'
// What the rep sent on this link (13411491362). Scopes the page, nothing more:
// the session can still read the whole record, see signingSections.ts.
export const SIGNING_SECTIONS_KEY = 'ecohomeSigningSections'
// How long RequireAuth shows the boot spinner alone before offering a reload.
export const AUTH_SLOW_MS = 8000

const firebaseConfig: FirebaseOptions = {
  apiKey: process.env['REACT_APP_G_API_KEY']!,
  projectId: c.commonEnv.FB_PROJECT_ID,
  storageBucket:
    c.commonEnv.FB_PROJECT_ID + '.' + c.commonEnv.FB_ST_BUCKET_SUFFIX,
  authDomain: c.REACT_APP_FIREBASE_AUTH_DOMAIN,
}

export const app = initializeApp(firebaseConfig, 'UPS')

// Use initializeAuth later
// https://firebase.google.com/docs/auth/web/custom-dependencies
export const auth = getAuth(app)
export const signInWithEmailAndPassword = (email: string, pass: string) =>
  _signInWithEmailAndPassword(auth, email, pass)

export const signInWithCustomToken = (token: string) =>
  _signInWithCustomToken(auth, token)

// Built lazily and kept in memory only (V20): a getAuth() instance at module
// load meant every tab ran a second Auth that polled IndexedDB alongside the
// main one, for a feature only /admin uses. It signs out right after creating
// the account, so there is no session to persist. Cached because
// initializeAuth() throws if called twice on the same app.
let adminAuthCache: Auth | null = null
const getAdminAuth = (): Auth => {
  if (!adminAuthCache) {
    const adminApp = initializeApp(firebaseConfig, 'AdminSecondary')
    adminAuthCache = initializeAuth(adminApp, {
      persistence: inMemoryPersistence,
    })
  }
  return adminAuthCache
}

export const createUserAccount = async (
  email: string,
  password: string,
): Promise<{ uid: string; err?: string }> => {
  try {
    const adminAuth = getAdminAuth()
    const cred = await _createUserWithEmailAndPassword(
      adminAuth,
      email,
      password,
    )
    await adminAuth.signOut()
    return { uid: cred.user.uid }
  } catch (e) {
    return { uid: '', err: e instanceof Error ? e.message : String(e) }
  }
}

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null)
  const isMounted = r.useIsMounted()

  React.useEffect(() => {
    const unSubscribe = auth.onAuthStateChanged(
      (user: User | null) => {
        isMounted() && setAuthenticated(!!user)
        if (!!user) {
          c.dispatch(
            c.setSessionVal({
              key: 'isAuth',
              value: user.uid,
            }),
          )
        }
      },
      (e) => {
        // TODO: analytics
        console.log(e)
        isMounted() && setAuthenticated(false)
        auth.signOut()
      },
    )

    return () => {
      unSubscribe()
    }
  }, [isMounted])

  const [isSlow, setIsSlow] = useState(false)

  React.useEffect(() => {
    if (authenticated !== null) {
      return
    }
    const timer = setTimeout(() => setIsSlow(true), AUTH_SLOW_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [authenticated])

  const location = useLocation()

  // move to redux?
  // Rendering nothing here replaced public/index.html's boot spinner with a
  // blank page for as long as Auth took to restore the session, which on an
  // iPad with many tabs open could be indefinitely. Keep the same spinner up
  // and, past AUTH_SLOW_MS, offer a reload. Never redirect on the timeout:
  // the user is almost certainly still signed in, and a redirect would lose
  // their destination and bypass the signing pin below.
  if (authenticated === null) {
    return (
      <div id="app-boot" style={{ flexDirection: 'column', gap: 16 }}>
        <div id="app-boot-spinner" />
        {isSlow && (
          <>
            <span>Still loading…</span>
            <button onClick={() => window.location.reload()} type="button">
              Reload
            </button>
          </>
        )}
      </div>
    )
  }

  if (!authenticated) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // A customer-signing session (see SignContractPage) authenticates as the
  // same shared, fully-privileged account for every customer — nothing on
  // the Firebase side scopes it yet (V50). Until that's fixed, pin this
  // session client-side to only its own report; block navigating (by URL
  // edit, back/forward, or clicking anything) to any other route. Real reps
  // never hit this branch — their own uid is never SIGNER_UID.
  if (auth.currentUser?.uid === SIGNER_UID) {
    const allowedCustomerID = sessionStorage.getItem(SIGNING_CUSTOMER_ID_KEY)
    const allowedPath = allowedCustomerID
      ? `/customers/${allowedCustomerID}/report`
      : null
    // The `?mode=sign` param must be enforced too, not just the path —
    // stripping it by hand keeps the same pathname but flips every
    // `readOnly` gate off, which is exactly how this was bypassed once.
    const isSignMode =
      new URLSearchParams(location.search).get('mode') === 'sign'
    if (!allowedPath || location.pathname !== allowedPath || !isSignMode) {
      return allowedPath ? (
        <Navigate to={`${allowedPath}?mode=sign`} replace />
      ) : (
        <Navigate to="/login" replace />
      )
    }
  }

  // CAST: `children` or FC typings wack
  return (<>{children}</>) as any
}

/**
 * True when the current session is a public customer-signing session (see
 * SignContractPage). Every `readOnly` gate should OR this in rather than
 * trusting `?mode=sign` alone — the query param is trivially removable from
 * the URL bar, the signed-in account is not.
 */
export const useIsSigningSession = (): boolean => {
  const [isSigning, setIsSigning] = useState(
    auth.currentUser?.uid === SIGNER_UID,
  )

  React.useEffect(() => {
    const unSubscribe = auth.onAuthStateChanged((user: User | null) => {
      setIsSigning(user?.uid === SIGNER_UID)
    })
    return () => {
      unSubscribe()
    }
  }, [])

  return isSigning
}

/** null = a link from before sections: the full report, as before. */
export const readSigningSections = (): SigningSection[] | null => {
  const raw = sessionStorage.getItem(SIGNING_SECTIONS_KEY)
  if (raw === null) return null
  try {
    return sanitizeSigningSections(JSON.parse(raw)) || []
  } catch {
    return []
  }
}

/** Never throws: the backend answers for a missing token (signingAuth.ts). */
export const currentIdToken = async (): Promise<string | undefined> => {
  try {
    return await auth.currentUser?.getIdToken()
  } catch {
    return undefined
  }
}

export const db = getDatabase(app)

// export const logFireWrite = (
//   path: string,
//   type: 'new' | 'put' | 'remove' | 'set' | 'update',
//   data?: unknown,
// ): void => {
//   let msg = ''

//   if (type === 'new') {
//     msg = `${auth.currentUser?.email} created new customer: ${path}`
//   } else if (type === 'put') {
//     msg = `${auth.currentUser?.email} putting files at path ${path}`
//   } else if (type === 'remove') {
//     msg = `${auth.currentUser?.email} removing data at path ${path}`
//   } else if (type === 'set') {
//     msg = `${
//       auth.currentUser?.email
//     } writing (set) to path ${path} data: ${JSON.stringify(data, null, 2)}`
//   } else if (type === 'update') {
//     msg = `${
//       auth.currentUser?.email
//     } writing (update) to path ${path} data: ${JSON.stringify(data, null, 2)}`
//   } else {
//     throw new TypeError(`Wrong type ${type} provided to logFireWrite()`)
//   }

//   const logsRef = ref(db, 'Logs')

//   push(logsRef, msg)
// }

export interface AdapterOpts {
  queryConstraints?: QueryConstraint[]
}

const fb9to8RefAdapter = (
  v9Ref: V9DatabaseReference,
  { queryConstraints }: AdapterOpts = {},
): c.DatabaseRef => {
  // const path = v9Ref.toString().substring(v9Ref.root.toString().length - 1)

  return {
    child(key: string) {
      if (queryConstraints) {
        throw new Error('Cannot call child() after providing queryConstraints')
      }
      return fb9to8RefAdapter(child(v9Ref, key))
    },
    off() {
      off(v9Ref)
    },
    on(event, cb, errCb) {
      const _query = query(v9Ref, ...(queryConstraints || []))

      if (event === 'child_added') {
        onChildAdded(_query, cb, errCb)
      } else if (event === 'child_changed') {
        onChildChanged(_query, cb, errCb)
      } else if (event === 'child_removed') {
        onChildRemoved(_query, cb, errCb)
      } else if (event === 'value') {
        onValue(_query, cb, errCb)
      } else if (event === 'child_moved') {
        onChildMoved(_query, cb, errCb)
      } else {
        throw new Error(`fb9to8RefAdapter() -> Un-Expected event: ${event}`)
      }
    },
    once() {
      if (queryConstraints) {
        return get(query(v9Ref, ...queryConstraints))
      }
      return get(v9Ref)
    },
    remove() {
      if (queryConstraints) {
        throw new Error('Cannot call remove() after providing queryConstraints')
      }
      //  logFireWrite(path, 'remove')
      remove(v9Ref)
    },
    update(data) {
      if (queryConstraints) {
        throw new Error('Cannot call update() after providing queryConstraints')
      }

      //  logFireWrite(path, 'update', data)

      update(v9Ref, data)
    },
    limitToFirst(howMany: number) {
      return fb9to8RefAdapter(v9Ref, {
        queryConstraints: (queryConstraints || [])?.concat(
          limitToFirst(howMany),
        ),
      })
    },
    limitToLast(howMany: number) {
      return fb9to8RefAdapter(v9Ref, {
        queryConstraints: (queryConstraints || [])?.concat(
          limitToLast(howMany),
        ),
      })
    },
    orderByChild(child) {
      return fb9to8RefAdapter(v9Ref, {
        queryConstraints: (queryConstraints || [])?.concat(orderByChild(child)),
      })
    },
    async set(value, onComplete) {
      try {
        //  logFireWrite(path, 'set', value)
        await set(v9Ref, value)
        onComplete && onComplete(null)
      } catch (e) {
        if (onComplete) {
          onComplete(e instanceof Error ? e : new Error(c.processErr(e)))
        } else {
          throw e
        }
      }
    },
  }
}

// TODO: account for CyberCam
export const getCustomersDB = () => {
  const userEmail = auth.currentUser?.email
  if (!userEmail) {
    throw new ReferenceError(
      `userEmail not defined in getCustomersDB() -> ${userEmail}`,
    )
  }
  const isCyberCam = userEmail.endsWith('cybercamscctv.com')
  return ref(db, isCyberCam ? 'Customers-CyberCam' : 'Customers')
}
export const closersDB = ref(db, '/Closers')
export const settersDB = ref(db, '/Setters')
// export const logsDB = (path: string) => ref(db, `/NewLogs${path}`)

const database = {
  ref(key: string) {
    return fb9to8RefAdapter(ref(db, key))
  },
}

// @ts-expect-error
window.db = database

c.setDatabase(database)

const v9Storage = getStorage(app)

export const fireStorage: c.Storage = {
  ref(key: string) {
    return {
      getMetadata() {
        return storageGetMetadata(storageRef(v9Storage, key))
      },
      putFile(blob) {
        const uploadTask = uploadBytesResumable(
          storageRef(v9Storage, key),
          blob as unknown as Blob,
        )

        return {
          on(e, next, error, complete) {
            if (e !== 'state_changed') {
              throw new Error('Unknown event')
            }

            // The v9 SDK already splits progress, failure and completion into
            // three callbacks; these used to be funnelled into a single one,
            // which is why the web build reported finished uploads correctly
            // and native did not. Pass each one straight through.
            return uploadTask.on(
              'state_changed',
              (snapshot) => {
                next?.({
                  bytesTransferred: snapshot.bytesTransferred,
                  state: 'running',
                  totalBytes: snapshot.totalBytes,
                })
              },
              (err) => {
                console.error('[FireStorage] upload error:', err)
                error?.(err)
              },
              () => {
                complete?.()
              },
            )
          },
        }
      },
    }
  },
}
